// ─── Static Phone-Number snapshot (FE route-bend) ────────────────────────────
//
// The backend `GET /v1/workforce/:id?type=risk_signals` route and its handlers
// are left fully intact — RiskSignalsDetailsSheet still fires the fetch. This
// module only supplies a FIXED, GetContact-derived phone snapshot that the
// Phone Numbers tab renders for EVERY workforce individual, so the demo data is
// deterministic regardless of what the credit bureau / AI step returns.
//
// Source numbers + labels were taken from GetContact lookups of:
//   • +62 859-3476-9000  → saved as "Penipu - Penipuan"          (fraud)
//   • +62 859-0023-1000  → saved as "Nenek - Keluarga"           (22 mixed tags)
//   • +62 859-5639-2000  → saved as "Penipu Pinjol - Penipuan Pinjol" (pinjol fraud)
//
// The shape mirrors the backend `phone_numbers` payload so the tab's existing
// transforms (listed_numbers → table rows, discovered_contacts → label rows,
// overview → key takeaway + indicators) work without any other change.

const NUM_FRAUD = '+62 859-3476-9000'
const NUM_FAMILY = '+62 859-0023-1000'
const NUM_PINJOL = '+62 859-5639-2000'

// ─── Listed numbers (Associated phone number table) ──────────────────────────
const LISTED_NUMBERS = [
  {
    number: NUM_FRAUD,
    is_current: false,
    aging_months: 14,
    last_update_date: '2026-05-20',
  },
  {
    number: NUM_FAMILY,
    is_current: true,
    aging_months: 52,
    last_update_date: '2026-06-02',
  },
  {
    number: NUM_PINJOL,
    is_current: false,
    aging_months: 7,
    last_update_date: '2025-11-12',
  },
]

// ─── Discovered contact labels (Contact labels table) ────────────────────────
// Real GetContact tags from the attachments, plus a few context-consistent
// custom labels for the numbers whose tag list was sparse. Sorted by count
// (descending) so the most-saved labels surface first, like a real lookup.
const DISCOVERED_CONTACTS = [
  // Generic fraud/spam labels cut across multiple numbers (multi-chip rows).
  { label: 'Spam', count: 8, numbers: [NUM_PINJOL, NUM_FRAUD, NUM_FAMILY], extra: null },

  // +62 859-5639-2000 — pinjol fraud (no public tag list; derived from context)
  { label: 'Penipu Pinjol', count: 11, numbers: [NUM_PINJOL], extra: null },
  { label: 'Penipuan Pinjol', count: 6, numbers: [NUM_PINJOL], extra: null },
  { label: 'DC Pinjol', count: 4, numbers: [NUM_PINJOL], extra: null },

  // +62 859-3476-9000 — "Penipu - Penipuan" (tag: "Penipu Gk Jelas")
  { label: 'Penipu', count: 7, numbers: [NUM_FRAUD, NUM_PINJOL], extra: null },
  { label: 'Penipu Gk Jelas', count: 4, numbers: [NUM_FRAUD], extra: null },
  { label: 'Penipuan', count: 3, numbers: [NUM_FRAUD, NUM_PINJOL], extra: null },

  // +62 859-0023-1000 — "Nenek - Keluarga" (curated subset of 22 tags)
  { label: 'Nenek', count: 9, numbers: [NUM_FAMILY], extra: null },
  { label: 'Sindikat Penipuan Online', count: 6, numbers: [NUM_FAMILY, NUM_PINJOL], extra: null },
  { label: 'Nenek Cucu', count: 5, numbers: [NUM_FAMILY], extra: null },
  { label: 'Mamah', count: 5, numbers: [NUM_FAMILY], extra: null },
  { label: 'Kakek', count: 4, numbers: [NUM_FAMILY], extra: null },
  { label: 'Abah', count: 4, numbers: [NUM_FAMILY], extra: null },
  { label: 'Mama Sumedang', count: 3, numbers: [NUM_FAMILY], extra: null },
  { label: 'Bu Katmawi', count: 3, numbers: [NUM_FAMILY], extra: null },
  { label: 'Nyokap Acil', count: 2, numbers: [NUM_FAMILY], extra: null },
  { label: 'Pa Katmawi', count: 2, numbers: [NUM_FAMILY], extra: null },
  { label: 'Ginanjar', count: 2, numbers: [NUM_FAMILY], extra: null },
  { label: 'Eceu Cucu Katmawi', count: 2, numbers: [NUM_FAMILY], extra: null },
]

// ─── Overview (Key takeaway + Risk assessment indicators) ────────────────────
const OVERVIEW = {
  key_takeaway:
    '3 numbers are linked to this individual. Most labels from external contacts '
    + 'flag fraud, spam and suspicious online-lending (pinjol) activity — including '
    + 'a "Sindikat Penipuan Online" (online fraud syndicate) tag. One number (…1000) '
    + 'has been active long-term, but the other two carry high-risk indicators.',
  // Matches the Figma layout: the first three rows render an "X / 10" score
  // (RiskAssessmentTable shows `value` as right-aligned text), Overall risk
  // renders a colored chip. Scores are derived for this fraud-heavy snapshot.
  risk_indicators: [
    {
      label: 'Risk labels detected',
      value: '8 / 10',
      tooltip:
        'How heavily external contacts have flagged these numbers with fraud/spam '
        + 'labels (e.g. "Penipu Gk Jelas", "Sindikat Penipuan Online", "Penipu Pinjol"). '
        + 'Higher means more risk labels surfaced.',
    },
    {
      label: 'Label consistency score',
      value: '2 / 10',
      tooltip:
        'How consistent the labels are across contacts. A low score means conflicting '
        + 'labels (family names mixed with fraud tags), lowering identity confidence.',
    },
    {
      label: 'Longest active number',
      value: '4 / 10',
      tooltip:
        'How established the longest-running linked number is relative to typical tenure. '
        + 'The most active number here (…1000) spans 52 months.',
    },
    {
      label: 'Overall risk',
      badge: 'High',
      badgeColor: 'error',
      tooltip:
        'Aggregated assessment based on the prevalence of fraud/spam labels and the '
        + 'activity span of the linked numbers.',
    },
  ],

}

const STATIC_PHONE_SECTION = {
  overview: OVERVIEW,
  listed_numbers: LISTED_NUMBERS,
  discovered_contacts: DISCOVERED_CONTACTS,
}

export default STATIC_PHONE_SECTION
